import React from 'react';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { ArrowRight, Truck, DollarSign, Code, Megaphone, Mountain, CarFront } from 'lucide-react';

export const CompaniesSection = () => {
  const companies = [
    {
      icon: Truck,
      name: 'SAI Fleet',
      tagline: 'Rent-to-Own Vehicle Solutions',
      description:
        'Specializing in rent-to-own vehicle programs, providing flexible mobility solutions for individuals, entrepreneurs, and businesses. Our model makes vehicle ownership accessible while supporting income generation.',
      features: [
        'Rent-to-own vehicles',
        'Fleet solutions for SMEs',
        'Flexible repayment structures',
        'Business mobility support',
      ],
      color: 'bg-blue-500/10 text-blue-600',
    },
    {
      icon: CarFront,
      name: 'Cars on Terms',
      tagline: 'Affordable Vehicle Ownership',
      description:
        'Dedicated to making car ownership affordable and transparent for the public. We provide flexible vehicle financing options tailored to different income levels, enabling reliable transport access.',
      features: [
        'Affordable vehicle financing',
        'Flexible payment terms',
        'New and pre-owned vehicles',
        'Consumer-focused solutions',
      ],
      color: 'bg-green-500/10 text-green-600',
    },
    {
      icon: Code,
      name: 'SAI IT',
      tagline: 'Full-Stack Software & App Development',
      description:
        'The technology backbone of the group, delivering full-stack software development, mobile applications, enterprise systems, and digital platforms for businesses across Africa and beyond.',
      features: [
        'Full-stack web and app development',
        'Enterprise software solutions',
        'AI and automation',
        'Digital transformation',
      ],
      color: 'bg-purple-500/10 text-purple-600',
    },
    {
      icon: DollarSign,
      name: 'VO Capital',
      tagline: 'Funding & Strategic Capital Solutions',
      description:
        'Provides funding and investment solutions for small, medium, and large corporations. We focus on empowering scalable businesses through structured capital and strategic partnerships.',
      features: [
        'SME funding and growth capital',
        'Corporate and project finance',
        'Strategic investments',
        'Expansion capital across Africa',
      ],
      color: 'bg-amber-500/10 text-amber-600',
    },
    {
      icon: Megaphone,
      name: 'SAI Media',
      tagline: 'Advertising, Marketing & Media',
      description:
        'A full-service media and marketing agency offering data-driven advertising, branding, and communication strategies designed to deliver measurable impact across multiple platforms.',
      features: [
        'Advertising and brand strategy',
        'Digital and outdoor media',
        'Marketing analytics',
        'Pan-African media reach',
      ],
      color: 'bg-pink-500/10 text-pink-600',
    },
    {
      icon: Mountain,
      name: 'SAI Comex',
      tagline: 'Mining, Minerals & Commodities',
      description:
        'Operates within the mining, minerals, and commodities sector, focusing on responsible resource development, trade, and value chain optimization across Africa\'s mineral-rich regions.',
      features: [
        'Mining and mineral exploration',
        'Commodities trading',
        'Strategic resource partnerships',
        'Sustainable operations',
      ],
      color: 'bg-orange-500/10 text-orange-600',
    },
  ];

  return (
    <section id="companies" className="section-padding bg-background">
      <div className="container-custom">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-4">
            Our{' '}
            <span className="text-primary">
              Companies
            </span>
          </h2>
          <div className="h-1 w-20 bg-primary mx-auto mb-6 rounded-full"></div>
          <p className="text-lg text-muted-foreground">
            Six dynamic companies delivering excellence across multiple sectors
          </p>
        </div>

        {/* Companies Grid */}
        <div className="grid lg:grid-cols-2 gap-8">
          {companies.map((company, index) => {
            const Icon = company.icon;
            return (
              <Card
                key={index}
                className="p-8 card-hover bg-card border-border flex flex-col"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className={`${company.color} p-3 rounded-lg`}>
                    <Icon className="w-8 h-8" />
                  </div>
                  <Badge variant="secondary" className="text-xs">
                    Active
                  </Badge>
                </div>

                <h3 className="text-2xl font-bold text-foreground mb-2">{company.name}</h3>
                <p className="text-sm font-medium text-primary mb-4">{company.tagline}</p>
                <p className="text-muted-foreground leading-relaxed mb-6">
                  {company.description}
                </p>

                <div className="space-y-2 mb-6">
                  <p className="text-sm font-semibold text-foreground">Key Focus Areas:</p>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {company.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start text-sm text-muted-foreground">
                        <span className="text-primary mr-2 flex-shrink-0">✓</span>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="mt-auto">
                  <Button
                    variant="outline"
                    className="w-full sm:w-auto border-primary text-primary hover:bg-primary hover:text-primary-foreground"
                  >
				  <a href="https://saifleet.co.za/">
                    Learn More</a>
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default CompaniesSection;